import { trackEvent, TrackingType } from 'tracking/events'
import { getCookie } from 'utils/cookie'

export enum HomePageAction {
  PAGE_LOAD_EVENT = 'Page Load',
  REG_INFO_EVENT = 'Reg Info',
  GOOGLE_ONE_TAP_LOAD = 'Google_Layer_Shown',
  GOOGLE_ONE_TAP_MISSED = 'Google_Layer_Not_Shown',
  GOOGLE_ONE_TAP_CLICK = 'Google_Account_Clicked',
  GOOGLE_ONE_TAP_CLOSED = 'Google_Layer_Closed',
  GOOGLE_ONE_TAP_SIGNUP = 'Google_Reg_Shown',
  GOOGLE_ONE_TAP_LOGIN = 'Google_Login_Success',
  BUTTON_CLICK = 'Click',
  BLUR_EVENT = 'Focus OUT',
  NEXT_SUCCESS_EVENT = 'Next_Success',
}

const getSchema = () => 'reg_page_tracking'

interface EventData {
  fieldName?: string
  fieldValue?: string
}

interface HomePageActionOptions {
  action: HomePageAction
  trackingType?: TrackingType
  eventData?: Record<keyof EventData, any>
}

export const trackAction = ({
  trackingType = TrackingType.BATCHED,
  action,
  eventData,
}: HomePageActionOptions) =>
  trackEvent({
    type: trackingType,
    schema: getSchema(),
    payload: {
      event_name: action,
      form_name: 'direct_homepage',
      field_name: eventData.fieldName,
      field_value: eventData.fieldValue,
    },
  })

// On Page Load
export const trackOnPageLoad = () =>
  trackAction({
    action: HomePageAction.PAGE_LOAD_EVENT,
    eventData: {
      fieldValue: window.location.href,
      fieldName: HomePageAction.PAGE_LOAD_EVENT,
    },
  })

// On Page Load track Reg Info - User country based on Ip address
export const trackRegInfoOnPageLoad = (domainCountry: string) =>
  trackAction({
    action: HomePageAction.REG_INFO_EVENT,
    eventData: {
      fieldValue: domainCountry,
      fieldName: 'user_country',
    },
  })

// On Google One Tap shown
export const trackGoogleOneTapShown = () =>
  trackAction({
    action: HomePageAction.GOOGLE_ONE_TAP_LOAD,
    eventData: {
      fieldValue: 'google_one_tap',
      fieldName: 'google_one_tap',
    },
  })

// On Google One Tap Not shown due to some reason
export const trackGoogleOneTapNotShown = (reason: string) =>
  trackAction({
    action: HomePageAction.GOOGLE_ONE_TAP_MISSED,
    eventData: {
      fieldValue: reason,
      fieldName: 'google_one_tap',
    },
  })

// On Google One Tap Clicked
export const trackGoogleOneTapClicked = ({
  fieldName,
  fieldValue,
}: EventData) =>
  trackAction({
    action: HomePageAction.GOOGLE_ONE_TAP_CLICK,
    eventData: {
      fieldValue,
      fieldName,
    },
  })

// On Google One Tap Closed
export const trackGoogleOneTapClosed = (reason: string) =>
  trackAction({
    action: HomePageAction.GOOGLE_ONE_TAP_CLOSED,
    eventData: {
      fieldValue: reason,
      fieldName: 'google_one_tap',
    },
  })

// On Reg 1.1 shown for Google Id
export const trackGoogleOneTapSignUp = ({ fieldName, fieldValue }: EventData) =>
  trackAction({
    action: HomePageAction.GOOGLE_ONE_TAP_SIGNUP,
    eventData: {
      fieldValue,
      fieldName,
    },
  })

// On login with Google Id
export const trackGoogleOneTapLogin = ({ fieldName, fieldValue }: EventData) =>
  trackAction({
    action: HomePageAction.GOOGLE_ONE_TAP_LOGIN,
    eventData: {
      fieldValue,
      fieldName,
    },
  })

// On Button Click
export const trackButtonClick = (eventData: EventData) =>
  trackAction({
    action: HomePageAction.BUTTON_CLICK,
    eventData: {
      fieldValue: eventData.fieldValue,
      fieldName: 'Button',
    },
  })

// On Next Success
export const trackNextSuccess = () =>
  trackAction({
    action: HomePageAction.NEXT_SUCCESS_EVENT,
    eventData: {
      fieldValue: window.location.href,
      fieldName: 'Next_Success',
    },
  })

// On Blur
export const trackOnBlur = (eventData: EventData) =>
  trackAction({
    action: HomePageAction.BLUR_EVENT,
    eventData: {
      fieldValue: eventData.fieldValue,
      fieldName: eventData.fieldName,
    },
  })

// On Page Load track banner displayed
export const trackBannerViewOnPageLoad = () =>
  trackAction({
    action: HomePageAction.PAGE_LOAD_EVENT,
    eventData: {
      fieldName: 'banner_view',
      fieldValue: getCookie('tvcView') || 'default',
    },
  })
